'use strict';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import api from '../api/api';
import {
  buildVentaPosCartSignature,
  buildVentaPosPayload,
  crearVentaPos,
  createVentaPosClientSaleId,
  getVentaPosError,
  imprimirDteVentaPos,
} from '../api/ventasPos';
import { readAuthSession } from '../auth/authStorage';
import NavbarPos from '../Components/NavbarPos';
import SearchBar from '../Components/SearchBar';
import CategoryBar from '../Components/CategoryBar';
import ProductGrid from '../Components/ProductGrid';
import Cart from '../Components/Cart';
import PaymentPanel from '../Components/PaymentPanel';
import PaymentModal from '../Components/PaymentModal';
import WeightModal from '../Components/WeightModal';
import ClienteFacturaModal, { validarClienteFactura } from '../Components/ClienteFacturaModal';
import CajaModal from '../Components/CajaModal';
import '../css/POS.css';

const GENERIC_RUT = '66.666.666-6';
const SEARCH_LIMIT = 30;
const CATEGORY_LIMIT = 6;
const WEIGHT_BARCODE_PATTERN = /^(220|230)\d{10}$/;
// Formatos soportados para etiquetas EAN-13 de balanza:
// 220/230 + PLU(5) + gramos(4) + dígito final
// 220/230 + PLU(4) + gramos(5) + dígito final
const WEIGHT_BARCODE_LAYOUTS = [
  { key: 'PLU5_WEIGHT4', productStart: 3, productEnd: 8, weightStart: 8, weightEnd: 12 },
  { key: 'PLU4_WEIGHT5', productStart: 3, productEnd: 7, weightStart: 7, weightEnd: 12 },
];
const GLOBAL_CODE_IDLE_MS = 5000;
const GLOBAL_CODE_GAP_MS = 3000;
const OFFLINE_DB_NAME = 'IntegraNubePOS';
const OFFLINE_DB_VERSION = 3;
const POS_PRODUCTS_STORE = 'pos_products';
const POS_CONFIG_STORE = 'pos_config';
const POS_PRODUCT_CACHE_BATCH_LIMIT = 2000;
const POS_PRODUCT_CACHE_FULL_TTL_MS = 24 * 60 * 60 * 1000;
const POS_PRODUCT_CACHE_DELTA_INTERVAL_MS = 5 * 60 * 1000;
const POS_PRODUCT_CACHE_OVERLAP_MS = 5000;
// Incrementar cuando cambie el payload persistido de /pos-cache.
// v2 incorpora imagen_publica / primera_imagen_publica en IndexedDB.
const POS_PRODUCT_CACHE_PAYLOAD_VERSION = 2;
let posProductsSyncPromise = null;
const posCategoryMemoryCache = new Map();

function clearCategoryMemoryCache(tenantId = null) {
  if (!tenantId) {
    posCategoryMemoryCache.clear();
    return;
  }

  const prefix = `${tenantId}:`;
  Array.from(posCategoryMemoryCache.keys()).forEach((key) => {
    if (String(key).startsWith(prefix)) posCategoryMemoryCache.delete(key);
  });
}

const INITIAL_LOAD_STEPS = [
  { key: 'caja', label: 'Verificando estado de caja' },
  { key: 'pagos', label: 'Cargando medios de pago' },
  { key: 'cliente', label: 'Preparando cliente de venta' },
  { key: 'categorias', label: 'Cargando categorías rápidas' },
  { key: 'productos', label: 'Cargando productos destacados' },
];

const createInitialLoadState = () => ({
  caja: { status: 'pending', detail: 'En espera' },
  pagos: { status: 'pending', detail: 'En espera' },
  cliente: { status: 'pending', detail: 'En espera' },
  categorias: { status: 'pending', detail: 'En espera' },
  productos: { status: 'pending', detail: 'En espera' },
  cache: { status: 'pending', detail: 'Se sincronizará en segundo plano' },
});

function parseBoolish(value, fallback = false) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'si', 'sí', 'yes', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  return fallback;
}

function getBarcode(product = {}) {
  return product.codigo_barra || product.codigo_barras || product.codigoBarra || product.codigoBarras || product.barcode || product.ean || product.upc || '';
}

function normalizeProduct(product = {}) {
  const id = product.id || product.id_producto || product.product_id || null;
  const taxes = Array.isArray(product.impuestos)
    ? product.impuestos
    : Array.isArray(product.taxes)
      ? product.taxes
      : [];

  const taxPercent = Number(product.impuesto_porcentaje ?? taxes[0]?.porcentaje ?? taxes[0]?.pct ?? 0) || 0;
  const hasGrossPrice = product.precio_bruto !== undefined && product.precio_bruto !== null;
  const netFromApi = Number(product.precio_neto ?? 0) || 0;
  const gross = Number(
    product.precio_bruto ??
    product.price ??
    product.precio ??
    (taxPercent > 0 ? netFromApi * (1 + taxPercent / 100) : netFromApi)
  ) || 0;

  const net = Number(product.precio_neto ?? (taxPercent > 0 ? gross / (1 + taxPercent / 100) : gross)) || 0;

  return {
    ...product,
    id,
    nombre: product.nombre || product.name || product.descripcion || product.sku || 'Producto',
    sku: product.sku || product.codigo_sku || product.codigo || '',
    codigoBarras: getBarcode(product),
    precioBruto: gross,
    precioNeto: net,
    precioBrutoConfirmado: hasGrossPrice || taxPercent > 0,
    impuestoPorcentaje: taxPercent,
    stock: product.stock_actual ?? product.stock ?? product.stock_disponible ?? null,
    imagen: product.imagen_publica || product.primera_imagen_publica || product.imagen_principal || product.imagen || '',
    categorias: Array.isArray(product.categorias) ? product.categorias : Array.isArray(product.categories) ? product.categories : [],
    pesable: parseBoolish(product.pesable ?? product.isWeight, false),
    permitePrecioVariable: parseBoolish(product.permite_precio_variable ?? product.permitePrecioVariable, false),
  };
}

function isSellableProduct(product = {}) {
  const status = String(product.estado || '').trim().toUpperCase();
  if (['BORRADOR', 'INACTIVO', 'ELIMINADO', 'ANULADO'].includes(status)) return false;

  const normalized = normalizeProduct(product);
  if (normalized.precioBruto <= 0 && !normalized.permitePrecioVariable) return false;
  return true;
}

function extractRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.productos)) return data.productos;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

function normalizeSearchText(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function parseWeightBarcodeCandidates(rawCode) {
  const code = String(rawCode || '').replace(/\D/g, '');
  if (!WEIGHT_BARCODE_PATTERN.test(code)) return [];

  return WEIGHT_BARCODE_LAYOUTS
    .map((layout) => {
      const productCode = code.slice(layout.productStart, layout.productEnd);
      const grams = Number(code.slice(layout.weightStart, layout.weightEnd));
      const productCodes = [...new Set([
        productCode,
        productCode.replace(/^0+(?=\d)/, ''),
      ].filter(Boolean))];

      return {
        ...layout,
        barcode: code,
        prefix: code.slice(0, 3),
        productCode,
        productCodes,
        grams,
        weightKg: grams / 1000,
        checkDigit: code.slice(-1),
      };
    })
    .filter((candidate) => candidate.grams > 0 && candidate.productCodes.length > 0);
}

function roundWeightKg(value) {
  return Math.round((Number(value) || 0) * 1000) / 1000;
}

function getTenantIdForCache() {
  return String(readAuthSession()?.tenantid || '').trim();
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('INDEXEDDB_REQUEST_ERROR'));
  });
}

function waitTransaction(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('INDEXEDDB_TRANSACTION_ERROR'));
    transaction.onabort = () => reject(transaction.error || new Error('INDEXEDDB_TRANSACTION_ABORTED'));
  });
}

function openPosCacheDb() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('INDEXEDDB_NO_DISPONIBLE'));
      return;
    }

    const request = window.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      let productsStore;

      if (!db.objectStoreNames.contains(POS_PRODUCTS_STORE)) {
        productsStore = db.createObjectStore(POS_PRODUCTS_STORE, { keyPath: 'cacheKey' });
      } else {
        productsStore = request.transaction.objectStore(POS_PRODUCTS_STORE);
      }

      if (!productsStore.indexNames.contains('by_tenant')) {
        productsStore.createIndex('by_tenant', 'tenantId', { unique: false });
      }
      if (!productsStore.indexNames.contains('by_tenant_id')) {
        productsStore.createIndex('by_tenant_id', ['tenantId', 'productId'], { unique: true });
      }
      if (!productsStore.indexNames.contains('by_tenant_sku')) {
        productsStore.createIndex('by_tenant_sku', ['tenantId', 'skuNorm'], { unique: false });
      }
      if (!productsStore.indexNames.contains('by_tenant_barcode')) {
        productsStore.createIndex('by_tenant_barcode', ['tenantId', 'barcodeNorm'], { unique: false });
      }

      let configStore;
      if (!db.objectStoreNames.contains(POS_CONFIG_STORE)) {
        configStore = db.createObjectStore(POS_CONFIG_STORE, { keyPath: 'cacheKey' });
      } else {
        configStore = request.transaction.objectStore(POS_CONFIG_STORE);
      }
      if (!configStore.indexNames.contains('by_tenant')) {
        configStore.createIndex('by_tenant', 'tenantId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('INDEXEDDB_OPEN_ERROR'));
  });
}

function buildCachedProductRecord(raw = {}, tenantId, generation = null) {
  const normalized = normalizeProduct(raw);
  const productId = normalized.id;
  if (productId === undefined || productId === null || productId === '') return null;

  const equivalenceText = Array.isArray(raw.equivalencias)
    ? raw.equivalencias
      .flatMap((item) => [item?.sku, item?.codigo_barras, item?.codigo, item?.nombre])
      .filter(Boolean)
      .join(' ')
    : '';

  return {
    ...normalized,
    cacheKey: `${tenantId}:${productId}`,
    tenantId,
    productId,
    skuNorm: normalizeSearchText(normalized.sku),
    barcodeNorm: normalizeSearchText(normalized.codigoBarras),
    searchText: normalizeSearchText(`${normalized.nombre} ${normalized.sku} ${normalized.codigoBarras} ${equivalenceText}`),
    generation,
  };
}

async function getProductsSyncMeta() {
  const tenantId = getTenantIdForCache();
  if (!tenantId) return null;

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_CONFIG_STORE, 'readonly');
    const row = await idbRequest(tx.objectStore(POS_CONFIG_STORE).get(`${tenantId}:products_sync_meta`));
    return row?.value || null;
  } finally {
    db.close();
  }
}

async function saveProductsSyncMeta(value) {
  const tenantId = getTenantIdForCache();
  if (!tenantId) return;

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_CONFIG_STORE, 'readwrite');
    const done = waitTransaction(tx);
    tx.objectStore(POS_CONFIG_STORE).put({
      cacheKey: `${tenantId}:products_sync_meta`,
      tenantId,
      value,
    });
    await done;
  } finally {
    db.close();
  }
}

async function cachePosProductsPage(rows = [], { generation = null } = {}) {
  const tenantId = getTenantIdForCache();
  if (!tenantId || !rows.length) return;

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_PRODUCTS_STORE, 'readwrite');
    const done = waitTransaction(tx);
    const store = tx.objectStore(POS_PRODUCTS_STORE);

    rows.forEach((raw) => {
      const productId = raw?.id ?? raw?.id_producto ?? raw?.product_id;
      if (productId === undefined || productId === null || productId === '') return;

      const cacheKey = `${tenantId}:${productId}`;
      if (raw?.deleted === true) {
        store.delete(cacheKey);
        return;
      }

      const record = buildCachedProductRecord(raw, tenantId, generation);
      if (record) store.put(record);
    });

    await done;
    clearCategoryMemoryCache(tenantId);
  } finally {
    db.close();
  }
}

async function finalizePosProductsGeneration(generation) {
  const tenantId = getTenantIdForCache();
  if (!tenantId || !generation) return;

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_PRODUCTS_STORE, 'readwrite');
    const done = waitTransaction(tx);
    const index = tx.objectStore(POS_PRODUCTS_STORE).index('by_tenant');
    const range = window.IDBKeyRange.only(tenantId);
    const request = index.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (cursor.value?.generation !== generation) cursor.delete();
      cursor.continue();
    };

    await done;
    clearCategoryMemoryCache(tenantId);
  } finally {
    db.close();
  }
}

async function getCachedProductsBySkus(skus = []) {
  const tenantId = getTenantIdForCache();
  const normalizedSkus = [...new Set(skus.map(normalizeSearchText).filter(Boolean))];
  if (!tenantId || !normalizedSkus.length) return new Map();

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_PRODUCTS_STORE, 'readonly');
    const index = tx.objectStore(POS_PRODUCTS_STORE).index('by_tenant_sku');
    const requests = normalizedSkus.map((sku) => idbRequest(index.get([tenantId, sku])));
    const results = await Promise.all(requests);

    const map = new Map();
    results.forEach((product, indexResult) => {
      if (product) map.set(normalizedSkus[indexResult], normalizeProduct(product));
    });
    return map;
  } finally {
    db.close();
  }
}

async function getCachedPosProductExact(term) {
  const tenantId = getTenantIdForCache();
  const query = normalizeSearchText(term);
  if (!tenantId || !query) return null;

  const db = await openPosCacheDb();
  try {
    for (const indexName of ['by_tenant_barcode', 'by_tenant_sku']) {
      const tx = db.transaction(POS_PRODUCTS_STORE, 'readonly');
      const index = tx.objectStore(POS_PRODUCTS_STORE).index(indexName);
      const result = await idbRequest(index.get([tenantId, query]));
      if (result) return normalizeProduct(result);
    }
    return null;
  } finally {
    db.close();
  }
}

async function searchCachedPosProducts(term, limit = SEARCH_LIMIT) {
  const tenantId = getTenantIdForCache();
  const query = normalizeSearchText(term);
  if (!tenantId || !query) return [];

  const exactCandidate = await getCachedPosProductExact(query).catch(() => null);
  const exact = exactCandidate && isSellableProduct(exactCandidate) ? exactCandidate : null;
  const results = exact ? [exact] : [];
  const usedIds = new Set(results.map((product) => String(product.id)));

  const db = await openPosCacheDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(POS_PRODUCTS_STORE, 'readonly');
      const index = tx.objectStore(POS_PRODUCTS_STORE).index('by_tenant');
      const request = index.openCursor(window.IDBKeyRange.only(tenantId));

      request.onerror = () => reject(request.error || new Error('INDEXEDDB_CURSOR_ERROR'));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || results.length >= limit) {
          resolve();
          return;
        }

        const product = cursor.value;
        const idKey = String(product?.productId ?? product?.id ?? '');
        if (
          !usedIds.has(idKey) &&
          isSellableProduct(product) &&
          String(product?.searchText || '').includes(query)
        ) {
          usedIds.add(idKey);
          results.push(normalizeProduct(product));
        }
        cursor.continue();
      };
    });

    return results.slice(0, limit);
  } finally {
    db.close();
  }
}

function productBelongsToCategory(product = {}, categoryId) {
  const target = String(categoryId || '').trim();
  if (!target) return true;

  const directIds = [product?.id_categoria, product?.categoria_id, product?.category_id]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map((value) => String(value));

  if (directIds.includes(target)) return true;

  const categories = Array.isArray(product?.categorias)
    ? product.categorias
    : Array.isArray(product?.categories)
      ? product.categories
      : [];

  return categories.some((category) => String(category?.id ?? category?.categoria_id ?? category?.category_id ?? '') === target);
}

async function getCachedCategoryProducts(categoryId, page = 1, limit = CATEGORY_LIMIT) {
  const tenantId = getTenantIdForCache();
  const targetCategory = String(categoryId || '').trim();
  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.max(1, Number(limit) || CATEGORY_LIMIT);

  if (!tenantId || !targetCategory) {
    return { productos: [], pagina: safePage, totalProductos: 0, totalPaginas: 1 };
  }

  const memoryKey = `${tenantId}:${targetCategory}`;
  let matches = posCategoryMemoryCache.get(memoryKey) || null;

  if (!matches) {
    matches = [];
    const db = await openPosCacheDb();

    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(POS_PRODUCTS_STORE, 'readonly');
        const index = tx.objectStore(POS_PRODUCTS_STORE).index('by_tenant');
        const request = index.openCursor(window.IDBKeyRange.only(tenantId));

        request.onerror = () => reject(request.error || new Error('INDEXEDDB_CURSOR_ERROR'));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) {
            resolve();
            return;
          }

          const product = cursor.value;
          if (isSellableProduct(product) && productBelongsToCategory(product, targetCategory)) {
            matches.push(normalizeProduct(product));
          }
          cursor.continue();
        };
      });
    } finally {
      db.close();
    }

    matches.sort((a, b) => {
      const idA = Number(a?.id);
      const idB = Number(b?.id);
      if (Number.isFinite(idA) && Number.isFinite(idB)) return idA - idB;
      return String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es');
    });

    posCategoryMemoryCache.set(memoryKey, matches);
  }

  const totalProductos = matches.length;
  const totalPaginas = Math.max(1, Math.ceil(totalProductos / safeLimit));
  const paginaFinal = Math.min(safePage, totalPaginas);
  const startIndex = (paginaFinal - 1) * safeLimit;

  return {
    productos: matches.slice(startIndex, startIndex + safeLimit),
    pagina: paginaFinal,
    totalProductos,
    totalPaginas,
  };
}

async function mergePosProductsIntoCache(rows = []) {
  const tenantId = getTenantIdForCache();
  if (!tenantId || !Array.isArray(rows) || !rows.length) return;

  const db = await openPosCacheDb();
  try {
    const tx = db.transaction(POS_PRODUCTS_STORE, 'readwrite');
    const done = waitTransaction(tx);
    const store = tx.objectStore(POS_PRODUCTS_STORE);

    rows.forEach((raw) => {
      const productId = raw?.id ?? raw?.id_producto ?? raw?.product_id;
      if (productId === undefined || productId === null || productId === '') return;

      const cacheKey = `${tenantId}:${productId}`;
      const getRequest = store.get(cacheKey);
      getRequest.onsuccess = () => {
        const existing = getRequest.result || {};
        const mergedRaw = {
          ...existing,
          ...raw,
          categorias: Array.isArray(raw?.categorias) && raw.categorias.length
            ? raw.categorias
            : (existing?.categorias || []),
          id_categoria: raw?.id_categoria ?? existing?.id_categoria ?? null,
          categoria_id: raw?.categoria_id ?? existing?.categoria_id ?? null,
          imagen_publica: raw?.imagen_publica || existing?.imagen || existing?.imagen_publica || '',
        };
        const record = buildCachedProductRecord(mergedRaw, tenantId, existing?.generation || null);
        if (record) store.put(record);
      };
    });

    await done;
    clearCategoryMemoryCache(tenantId);
  } finally {
    db.close();
  }
}

async function performPosProductsSync({ forceFull = false } = {}) {
  if (typeof window === 'undefined' || !window.indexedDB) return { skipped: 'indexeddb' };
  if (!navigator.onLine) return { skipped: 'offline' };

  const tenantId = getTenantIdForCache();
  if (!tenantId) return { skipped: 'tenant' };

  const meta = (await getProductsSyncMeta()) || {};
  const now = Date.now();
  const lastFullMs = meta?.lastFullSync ? Date.parse(meta.lastFullSync) : 0;
  const payloadVersion = Number(meta?.payloadVersion || 0);
  const payloadChanged = payloadVersion !== POS_PRODUCT_CACHE_PAYLOAD_VERSION;
  const needsFull = forceFull || payloadChanged || !lastFullMs || !Number.isFinite(lastFullMs) || (now - lastFullMs) >= POS_PRODUCT_CACHE_FULL_TTL_MS;
  const updatedSince = needsFull ? null : (meta?.lastDeltaSync || meta?.lastFullSync || null);
  const generation = needsFull ? `g-${Date.now()}-${Math.random().toString(36).slice(2)}` : null;

  let afterId = 0;
  let totalChanged = 0;
  let serverTime = null;
  let guard = 0;

  while (guard < 1000) {
    guard += 1;
    const params = {
      limit: POS_PRODUCT_CACHE_BATCH_LIMIT,
      after_id: afterId,
    };
    if (updatedSince) params.updated_since = updatedSince;

    const { data } = await api.get('/inventario_productos/pos-cache', {
      params,
      timeout: 20000,
    });

    if (!serverTime) serverTime = data?.server_time || new Date().toISOString();

    const rows = extractRows(data);
    await cachePosProductsPage(rows, { generation });
    totalChanged += rows.length;

    const hasMore = data?.has_more === true;
    const nextCursor = data?.next_cursor;
    if (!hasMore || nextCursor === undefined || nextCursor === null || String(nextCursor) === String(afterId)) break;
    afterId = nextCursor;
  }

  if (needsFull) await finalizePosProductsGeneration(generation);

  const anchorTime = serverTime || new Date().toISOString();
  const anchorMs = Date.parse(anchorTime);
  const deltaAnchor = Number.isFinite(anchorMs)
    ? new Date(anchorMs - POS_PRODUCT_CACHE_OVERLAP_MS).toISOString()
    : new Date(Date.now() - POS_PRODUCT_CACHE_OVERLAP_MS).toISOString();

  await saveProductsSyncMeta({
    payloadVersion: POS_PRODUCT_CACHE_PAYLOAD_VERSION,
    lastFullSync: needsFull ? anchorTime : (meta?.lastFullSync || anchorTime),
    lastDeltaSync: deltaAnchor,
    lastMode: needsFull ? 'full' : 'delta',
    lastChanged: totalChanged,
  });

  return {
    mode: needsFull ? 'full' : 'delta',
    totalChanged,
    serverTime: anchorTime,
  };
}

function refreshPosProductsCache(options = {}) {
  if (posProductsSyncPromise) return posProductsSyncPromise;

  posProductsSyncPromise = performPosProductsSync(options)
    .finally(() => {
      posProductsSyncPromise = null;
    });

  return posProductsSyncPromise;
}

async function fetchInventoryFilters() {
  const { data } = await api.get('/inventario_productos/filtros', { timeout: 6000 });
  return Array.isArray(data?.categorias) ? data.categorias : [];
}

async function fetchCategoryProductsRemote(categoryId, page = 1) {
  const { data } = await api.get('/inventario_productos', {
    params: {
      page,
      limit: CATEGORY_LIMIT,
      cat_id: categoryId,
    },
    timeout: 8000,
  });

  const rows = extractRows(data)
    .filter(isSellableProduct)
    .map(normalizeProduct);

  return {
    productos: rows,
    pagina: Math.max(1, Number(page) || 1),
    totalProductos: Number(data?.totalProductos ?? data?.total ?? rows.length) || rows.length,
    totalPaginas: Math.max(1, Number(data?.totalPaginas ?? data?.pages ?? 1) || 1),
  };
}

async function fetchFeaturedProducts() {
  const { data } = await api.get('/inventario_productos/destacados', { timeout: 8000 });
  const rows = extractRows(data);

  let cachedBySku = new Map();
  try {
    cachedBySku = await getCachedProductsBySkus(rows.map((row) => row?.sku));
  } catch (_) {
    // El caché es una optimización; destacados debe seguir funcionando sin IndexedDB.
  }

  const featured = rows.map((row) => {
    const cached = cachedBySku.get(normalizeSearchText(row?.sku));
    return normalizeProduct({
      ...(cached || {}),
      ...row,
      origenDestacado: true,
    });
  });

  // /destacados prioriza información visual y puede no incluir precio bruto/impuesto.
  // En el primer uso (sin caché aún) enriquecemos esos pocos productos con /pos para
  // no mostrar ni usar precio neto como si fuera precio final.
  const unresolved = featured
    .map((product, index) => ({ product, index }))
    .filter(({ product }) => product?.sku && !product?.precioBrutoConfirmado);

  if (!unresolved.length) return featured;

  const details = await Promise.allSettled(
    unresolved.map(({ product }) => api.get('/inventario_productos/pos', {
      params: { search: product.sku, limit: SEARCH_LIMIT },
      timeout: 6000,
    }))
  );

  details.forEach((result, detailIndex) => {
    if (result.status !== 'fulfilled') return;

    const { product, index } = unresolved[detailIndex];
    const candidates = extractRows(result.value?.data).map(normalizeProduct);
    const exact = candidates.find((candidate) => normalizeSearchText(candidate.sku) === normalizeSearchText(product.sku));
    if (!exact) return;

    featured[index] = normalizeProduct({
      ...product,
      ...exact,
      imagen_publica: product.imagen || exact.imagen,
      origenDestacado: true,
    });
  });

  return featured;
}

async function resolveFeaturedProduct(product) {
  if (!product?.origenDestacado || !product?.sku) return product;

  const cached = await getCachedPosProductExact(product.sku).catch(() => null);
  if (cached) {
    return normalizeProduct({
      ...product,
      ...cached,
      imagen_publica: product.imagen || cached.imagen,
      origenDestacado: true,
    });
  }

  try {
    const { data } = await api.get('/inventario_productos/pos', {
      params: { search: product.sku, limit: SEARCH_LIMIT },
      timeout: 6000,
    });
    const rawRows = extractRows(data);
    mergePosProductsIntoCache(rawRows).catch(() => {});
    const rows = rawRows.map(normalizeProduct);
    const exact = rows.find((row) => normalizeSearchText(row.sku) === normalizeSearchText(product.sku));
    if (!exact) return product;

    return normalizeProduct({
      ...product,
      ...exact,
      imagen_publica: product.imagen || exact.imagen,
      origenDestacado: true,
    });
  } catch (_) {
    return product;
  }
}

async function resolveWeightBarcode(rawCode) {
  const candidates = parseWeightBarcodeCandidates(rawCode);
  if (!candidates.length) return null;

  // Primero IndexedDB: la lectura de una etiqueta de balanza debe ser instantánea
  // y no depender de la red cuando el catálogo local está sincronizado.
  for (const candidate of candidates) {
    for (const productCode of candidate.productCodes) {
      const cached = await getCachedPosProductExact(productCode).catch(() => null);
      if (cached?.pesable && isSellableProduct(cached)) {
        return { ...candidate, productCode, product: normalizeProduct(cached), source: 'cache' };
      }
    }
  }

  // Fallback online solamente cuando el PLU no existe todavía en el caché local.
  if (!navigator.onLine) return null;

  const attempted = new Set();
  for (const candidate of candidates) {
    for (const productCode of candidate.productCodes) {
      if (attempted.has(productCode)) continue;
      attempted.add(productCode);

      try {
        const { data } = await api.get('/inventario_productos/pos', {
          params: { search: productCode, limit: SEARCH_LIMIT },
          timeout: 6000,
        });
        const rawRows = extractRows(data);
        mergePosProductsIntoCache(rawRows).catch(() => {});
        const rows = rawRows.map(normalizeProduct);
        const expected = normalizeSearchText(productCode);
        const exact = rows.find((row) => row.pesable && (
          normalizeSearchText(row.sku) === expected ||
          normalizeSearchText(row.codigoBarras) === expected
        ));

        if (exact) {
          return { ...candidate, productCode, product: exact, source: 'api' };
        }
      } catch (_) {
        // Se prueban los demás formatos/códigos candidatos antes de fallar.
      }
    }
  }

  return null;
}

function normalizePaymentKey(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function normalizePaymentMethod(row = {}, index = 0) {
  const key = normalizePaymentKey(row.metodopago_clave || row.metodo_pago_clave || row.payment_method_key || row.clave || row.codigo || row.nombre || row.label);
  if (!key) return null;

  const relationId = Number(
    row.payment_relation_id ??
    row.relation_id ??
    row.relacion_id ??
    row.configuracion_metodo_condicion_id ??
    0
  );
  const methodId = Number(
    row.payment_method_id ??
    row.metodo_pago_id ??
    row.metodopago_id ??
    row.method_id ??
    0
  );
  const conditionId = Number(
    row.payment_condition_id ??
    row.condicion_pago_id ??
    row.condicionpago_id ??
    row.condition_id ??
    0
  );
  const conditionKey = normalizePaymentKey(
    row.payment_condition_key ??
    row.condicion_pago_clave ??
    row.condicionpago_clave ??
    row.condition_key
  );

  const safeRelationId = Number.isInteger(relationId) && relationId > 0 ? relationId : null;
  const safeMethodId = Number.isInteger(methodId) && methodId > 0 ? methodId : null;
  const safeConditionId = Number.isInteger(conditionId) && conditionId > 0 ? conditionId : null;

  return {
    ...row,
    // id es solo para selección UI. La relación enviada a ventas_pos usa el
    // campo payment_relation_id explícito y nunca asume que row.id sea relación.
    id: safeRelationId || safeMethodId || row.id || index + 1,
    payment_relation_id: safeRelationId,
    payment_method_id: safeMethodId,
    methodId: safeMethodId,
    payment_condition_id: safeConditionId,
    conditionId: safeConditionId,
    payment_condition_key: conditionKey || null,
    conditionKey: conditionKey || null,
    clave: key,
    nombre: row.metodopago_nombre || row.metodo_pago_valor || row.metodo_pago_nombre || row.payment_method_name || row.valor || row.nombre || row.label || key,
    visiblePos: row.visible_pos !== false,
    activo: row.activo !== false,
    permiteVuelto: parseBoolish(row.permite_vuelto ?? row.permiteVuelto, key === 'EFECTIVO'),
    requiereReferencia: parseBoolish(row.requiere_referencia ?? row.requiereReferencia, false),
    orden: Number(row.orden_pos ?? row.orden ?? index + 1),
  };
}

async function fetchPaymentMethods() {
  const endpoints = ['/tesoreria/metodos-contabilidad', '/tesoreria_metodospago/pos', '/tesoreria_metodospago'];
  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      const { data } = await api.get(endpoint, { params: { visible_pos: true, activo: true }, timeout: 6000 });
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.metodos)
            ? data.metodos
            : Array.isArray(data?.metodos_pago)
              ? data.metodos_pago
              : Array.isArray(data?.data)
                ? data.data
                : [];

      return rows
        .map(normalizePaymentMethod)
        .filter(Boolean)
        .filter((method) => method.activo && method.visiblePos)
        .sort((a, b) => a.orden - b.orden);
    } catch (error) {
      lastError = error;
      if (error?.response?.status === 404) continue;
      break;
    }
  }

  throw lastError || new Error('NO_PAYMENT_METHODS_ENDPOINT');
}

async function fetchGenericCustomer() {
  const { data } = await api.get('/ventas_clientes', {
    params: { rut: GENERIC_RUT, search: GENERIC_RUT, q: GENERIC_RUT, limit: 10 },
    timeout: 6000,
  });

  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.clientes)
      ? data.clientes
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
          ? data.data
          : [];

  return rows.find((row) => String(row?.rut || '').trim() === GENERIC_RUT) || rows[0] || null;
}

async function fetchCashStatus() {
  const { data } = await api.get('/pos_cajas/estado', { timeout: 6000 });
  const open = data?.abierta === true || data?.isAbierta === true || data?.caja_abierta === true || String(data?.estado || '').toUpperCase() === 'ABIERTA' || String(data?.caja?.estado || '').toUpperCase() === 'ABIERTA';
  return { abierta: open, caja: data?.caja || data?.data || data?.estado_caja || data || null };
}

export default function POS({ usuario, onSalir }) {
  const [conectado, setConectado] = useState(navigator.onLine);
  const [caja, setCaja] = useState({ abierta: false, caja: null });
  const [productos, setProductos] = useState([]);
  const [cargandoProductos, setCargandoProductos] = useState(true);
  const [totalProductos, setTotalProductos] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [categoriasDisponibles, setCategoriasDisponibles] = useState([]);
  const [cargandoCategorias, setCargandoCategorias] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [modoBusqueda, setModoBusqueda] = useState(false);
  const [categoria, setCategoria] = useState('');
  const [items, setItems] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [metodoSeleccionado, setMetodoSeleccionado] = useState('');
  const [cliente, setCliente] = useState(null);
  const [clienteGenerico, setClienteGenerico] = useState(null);
  const [modalClienteFactura, setModalClienteFactura] = useState(false);
  const [documento, setDocumento] = useState('boleta');
  const [documentoTemporal, setDocumentoTemporal] = useState('boleta');
  const [modalCobro, setModalCobro] = useState(false);
  const [procesandoVenta, setProcesandoVenta] = useState(false);
  const [modalDocumento, setModalDocumento] = useState(false);
  const [modalCaja, setModalCaja] = useState(false);
  const [modalPeso, setModalPeso] = useState(false);
  const [productoPeso, setProductoPeso] = useState(null);
  const [pesoInicialKg, setPesoInicialKg] = useState(null);
  const [pesoEditIndex, setPesoEditIndex] = useState(null);
  const [continuarPagoTrasDocumento, setContinuarPagoTrasDocumento] = useState(false);
  const [inicializando, setInicializando] = useState(true);
  const [estadoCargaInicial, setEstadoCargaInicial] = useState(createInitialLoadState);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
  const searchSequenceRef = useRef(0);
  const scannerBufferRef = useRef('');
  const scannerTimerRef = useRef(null);
  const scannerLastKeyAtRef = useRef(0);
  const scannerBusyRef = useRef(false);
  const procesandoVentaRef = useRef(false);
  const ventaIntentoRef = useRef(null);

  const notify = useCallback((message, severity = 'info') => setSnack({ open: true, severity, message }), []);
  const cajaId = caja?.caja?.id ?? caja?.caja?.caja_id ?? caja?.caja_id ?? caja?.id ?? null;

  const refreshCaja = useCallback(async () => {
    try {
      const next = await fetchCashStatus();
      setCaja(next);
      setConectado(true);
      return next;
    } catch (error) {
      setConectado(false);
      notify(error?.response?.data?.message || error?.response?.data?.error || 'No se pudo actualizar el estado de caja.', 'warning');
      return null;
    }
  }, [notify]);

  const loadFeatured = useCallback(async () => {
    const sequence = ++searchSequenceRef.current;
    setCargandoProductos(true);
    try {
      const rows = await fetchFeaturedProducts();
      if (sequence !== searchSequenceRef.current) return;

      setProductos(rows);
      setTotalProductos(rows.length);
      setPagina(1);
      setTotalPaginas(Math.max(1, Math.ceil(rows.length / CATEGORY_LIMIT)));
      setModoBusqueda(false);
      setConectado(true);
    } catch (error) {
      if (sequence !== searchSequenceRef.current) return;
      setConectado(false);
      setProductos([]);
      notify(error?.response?.data?.message || error?.response?.data?.error || 'No se pudieron cargar los productos destacados.', 'error');
    } finally {
      if (sequence === searchSequenceRef.current) setCargandoProductos(false);
    }
  }, [notify]);

  const loadCategory = useCallback(async (categoryId, page = 1) => {
    const id = String(categoryId || '').trim();
    if (!id) {
      await loadFeatured();
      return;
    }

    const sequence = ++searchSequenceRef.current;
    setCargandoProductos(true);

    try {
      let result = await getCachedCategoryProducts(id, page, CATEGORY_LIMIT);

      // En la primera ejecución el full sync puede seguir corriendo. Si todavía
      // no existe catálogo local, esperamos esa misma sincronización y reintentamos.
      if (result.totalProductos === 0 && navigator.onLine) {
        const meta = await getProductsSyncMeta().catch(() => null);
        if (!meta?.lastFullSync || posProductsSyncPromise) {
          await refreshPosProductsCache().catch(() => null);
          result = await getCachedCategoryProducts(id, page, CATEGORY_LIMIT);
        }
      }

      if (sequence !== searchSequenceRef.current) return;

      setProductos(result.productos);
      setTotalProductos(result.totalProductos);
      setPagina(result.pagina);
      setTotalPaginas(result.totalPaginas);
      setModoBusqueda(false);
    } catch (cacheError) {
      // IndexedDB es la fuente principal. Solo si el navegador no puede usarlo
      // degradamos a la API para no dejar el PDV inutilizable.
      if (sequence !== searchSequenceRef.current) return;

      if (navigator.onLine) {
        try {
          const result = await fetchCategoryProductsRemote(id, page);
          if (sequence !== searchSequenceRef.current) return;
          setProductos(result.productos);
          setTotalProductos(result.totalProductos);
          setPagina(result.pagina);
          setTotalPaginas(result.totalPaginas);
          setModoBusqueda(false);
          notify('No fue posible usar el catálogo local. Se utilizó la API temporalmente.', 'warning');
          return;
        } catch (_) {
          // Continúa al error local común.
        }
      }

      setProductos([]);
      setPagina(1);
      setTotalPaginas(1);
      notify(cacheError?.message || 'No se pudo consultar el catálogo local.', 'error');
    } finally {
      if (sequence === searchSequenceRef.current) setCargandoProductos(false);
    }
  }, [loadFeatured, notify]);

  const searchProducts = useCallback(async (term) => {
    const q = String(term || '').trim();
    if (!q) {
      await loadFeatured();
      return [];
    }

    const sequence = ++searchSequenceRef.current;
    setCargandoProductos(true);

    try {
      // La búsqueda normal y la pistola consultan IndexedDB primero.
      const cachedRows = await searchCachedPosProducts(q, SEARCH_LIMIT);
      if (sequence !== searchSequenceRef.current) return [];

      if (cachedRows.length) {
        setProductos(cachedRows);
        setModoBusqueda(true);
        setPagina(1);
        setTotalPaginas(Math.max(1, Math.ceil(cachedRows.length / CATEGORY_LIMIT)));
        setTotalProductos(cachedRows.length);
        return cachedRows;
      }

      // Si el catálogo local todavía se está preparando, esperamos el sync y
      // hacemos un segundo intento antes de tocar la API de búsqueda.
      if (navigator.onLine && posProductsSyncPromise) {
        await refreshPosProductsCache().catch(() => null);
        const syncedRows = await searchCachedPosProducts(q, SEARCH_LIMIT);
        if (sequence !== searchSequenceRef.current) return [];
        if (syncedRows.length) {
          setProductos(syncedRows);
          setModoBusqueda(true);
          setPagina(1);
          setTotalPaginas(Math.max(1, Math.ceil(syncedRows.length / CATEGORY_LIMIT)));
          setTotalProductos(syncedRows.length);
          return syncedRows;
        }
      }

      if (!navigator.onLine) {
        setProductos([]);
        setModoBusqueda(true);
        setPagina(1);
        setTotalPaginas(1);
        setTotalProductos(0);
        notify(`No se encontró “${q}” en el catálogo local.`, 'warning');
        return [];
      }

      // Fallback controlado para códigos/productos aún no sincronizados.
      const { data } = await api.get('/inventario_productos/pos', {
        params: { search: q, limit: SEARCH_LIMIT },
        timeout: 6000,
      });

      if (sequence !== searchSequenceRef.current) return [];

      const rawRows = extractRows(data);
      mergePosProductsIntoCache(rawRows).catch(() => {});
      const rows = rawRows.filter(isSellableProduct).map(normalizeProduct);
      setProductos(rows);
      setModoBusqueda(true);
      setPagina(1);
      setTotalPaginas(Math.max(1, Math.ceil(rows.length / CATEGORY_LIMIT)));
      setTotalProductos(Number(data?.total || rows.length) || rows.length);
      setConectado(true);
      return rows;
    } catch (error) {
      if (sequence !== searchSequenceRef.current) return [];

      setProductos([]);
      setModoBusqueda(true);
      setPagina(1);
      setTotalPaginas(1);
      setTotalProductos(0);
      notify(error?.response?.data?.message || error?.response?.data?.error || 'No se pudo buscar productos.', 'error');
      return [];
    } finally {
      if (sequence === searchSequenceRef.current) setCargandoProductos(false);
    }
  }, [loadFeatured, notify]);

  useEffect(() => {
    let active = true;

    const updateLoadStep = (key, status, detail) => {
      if (!active) return;
      setEstadoCargaInicial((current) => ({
        ...current,
        [key]: {
          ...(current[key] || {}),
          status,
          detail,
        },
      }));
    };

    const trackLoadStep = async (key, loadingDetail, successDetail, task) => {
      updateLoadStep(key, 'loading', loadingDetail);
      try {
        const value = await task();
        updateLoadStep(key, 'success', typeof successDetail === 'function' ? successDetail(value) : successDetail);
        return value;
      } catch (error) {
        updateLoadStep(key, 'error', error?.response?.data?.message || error?.response?.data?.error || 'No fue posible completar esta etapa');
        throw error;
      }
    };

    (async () => {
      setInicializando(true);
      setCargandoProductos(true);
      setEstadoCargaInicial(createInitialLoadState());

      const results = await Promise.allSettled([
        trackLoadStep(
          'caja',
          'Consultando caja activa…',
          (value) => value?.abierta ? 'Caja abierta y lista' : 'No hay una caja abierta',
          fetchCashStatus
        ),
        trackLoadStep(
          'pagos',
          'Consultando medios habilitados…',
          (value) => `${Array.isArray(value) ? value.length : 0} medio(s) disponible(s)`,
          fetchPaymentMethods
        ),
        trackLoadStep(
          'cliente',
          'Buscando cliente genérico…',
          (value) => value ? 'Cliente de venta preparado' : 'Cliente genérico no encontrado',
          fetchGenericCustomer
        ),
        trackLoadStep(
          'categorias',
          'Consultando categorías disponibles…',
          (value) => `${Array.isArray(value) ? value.length : 0} categoría(s) disponible(s)`,
          fetchInventoryFilters
        ),
        trackLoadStep(
          'productos',
          'Obteniendo productos destacados…',
          (value) => `${Array.isArray(value) ? value.length : 0} destacado(s) cargado(s)`,
          fetchFeaturedProducts
        ),
      ]);

      if (!active) return;

      const [cashResult, paymentsResult, customerResult, categoriesResult, productsResult] = results;

      if (cashResult.status === 'fulfilled') setCaja(cashResult.value);
      else setConectado(false);

      if (paymentsResult.status === 'fulfilled') {
        setMetodosPago(paymentsResult.value);
        const cashMethod = paymentsResult.value.find((method) => method.clave === 'EFECTIVO') || paymentsResult.value[0];
        if (cashMethod) setMetodoSeleccionado(String(cashMethod.id));
      }

      if (customerResult.status === 'fulfilled') {
        setCliente(customerResult.value);
        setClienteGenerico(customerResult.value);
      }

      if (categoriesResult.status === 'fulfilled') {
        setCategoriasDisponibles(categoriesResult.value);
      } else {
        setCategoriasDisponibles([]);
      }
      setCargandoCategorias(false);

      if (productsResult.status === 'fulfilled') {
        const rows = productsResult.value || [];
        setProductos(rows);
        setTotalProductos(rows.length);
        setPagina(1);
        setTotalPaginas(Math.max(1, Math.ceil(rows.length / CATEGORY_LIMIT)));
        setConectado(true);
      } else {
        setConectado(false);
        notify('No se pudieron cargar los productos destacados.', 'warning');
      }

      // Importante: la carga inicial de destacados ya terminó. Evita que ProductGrid
      // quede mostrando el spinner indefinidamente después de cerrar el modal.
      setCargandoProductos(false);

      // El caché POS se sincroniza en segundo plano. Se informa en el modal, pero no
      // bloquea el ingreso al Punto de Venta porque un full sync puede tardar bastante.
      updateLoadStep('cache', 'loading', 'Sincronizando catálogo local en segundo plano…');
      refreshPosProductsCache()
        .then((result) => {
          if (!active) return;
          if (result?.skipped) {
            const detail = result.skipped === 'offline'
              ? 'Pendiente: se sincronizará cuando vuelva la conexión'
              : 'Sincronización local no requerida';
            updateLoadStep('cache', 'success', detail);
            return;
          }
          updateLoadStep('cache', 'success', `${result.mode === 'full' ? 'Catálogo completo' : 'Cambios'} · ${result.totalChanged} registro(s)`);
          console.info(`[PDV cache] ${result.mode} · ${result.totalChanged} cambio(s) procesado(s).`);
        })
        .catch((error) => {
          if (!active) return;
          updateLoadStep('cache', 'error', 'El PDV seguirá operativo; el catálogo local se reintentará después');
          console.warn('[PDV cache] No fue posible sincronizar el catálogo local:', error?.message || error);
        });

      setInicializando(false);
    })();

    const onOnline = () => {
      setConectado(true);
      refreshPosProductsCache().catch(() => {});
    };
    const onOffline = () => setConectado(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const cacheInterval = window.setInterval(() => {
      if (navigator.onLine) refreshPosProductsCache().catch(() => {});
    }, POS_PRODUCT_CACHE_DELTA_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(cacheInterval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [notify]);

  const addWeightedItem = useCallback((product, weightKg, { source = 'manual', barcode = null } = {}) => {
    const safeWeight = roundWeightKg(weightKg);
    if (!product || safeWeight <= 0) return;

    setItems((current) => {
      const index = current.findIndex((item) => item?.pesable && String(item.id) === String(product.id));
      if (index === -1) {
        return [{
          ...product,
          pesable: true,
          cantidad: safeWeight,
          pesoKg: safeWeight,
          pesoOrigen: source,
          codigoBalanza: barcode || null,
        }, ...current];
      }

      return current.map((item, i) => {
        if (i !== index) return item;
        const nextWeight = roundWeightKg(Number(item.cantidad || 0) + safeWeight);
        return {
          ...item,
          cantidad: nextWeight,
          pesoKg: nextWeight,
          pesoOrigen: source,
          codigoBalanza: barcode || item.codigoBalanza || null,
        };
      });
    });
  }, []);

  const openWeightModal = useCallback((product, editIndex = null) => {
    if (!product) return;
    setProductoPeso(product);
    setPesoEditIndex(Number.isInteger(editIndex) ? editIndex : null);
    setPesoInicialKg(Number.isInteger(editIndex) ? Number(items[editIndex]?.cantidad || 0) : null);
    setModalPeso(true);
  }, [items]);

  const closeWeightModal = useCallback(() => {
    setModalPeso(false);
    setProductoPeso(null);
    setPesoInicialKg(null);
    setPesoEditIndex(null);
  }, []);

  const confirmWeight = useCallback((weightKg) => {
    const safeWeight = roundWeightKg(weightKg);
    if (!productoPeso || safeWeight <= 0) return;

    if (Number.isInteger(pesoEditIndex)) {
      setItems((rows) => rows.map((item, index) => index === pesoEditIndex
        ? { ...item, cantidad: safeWeight, pesoKg: safeWeight, pesoOrigen: 'manual' }
        : item));
    } else {
      addWeightedItem(productoPeso, safeWeight, { source: 'manual' });
    }

    closeWeightModal();
  }, [addWeightedItem, closeWeightModal, pesoEditIndex, productoPeso]);

  const addProduct = useCallback(async (product) => {
    if (!caja.abierta) {
      notify('Debe abrir caja antes de agregar productos.', 'warning');
      return;
    }

    const resolvedProduct = await resolveFeaturedProduct(product);

    if (resolvedProduct.permitePrecioVariable && Number(resolvedProduct.precioBruto || 0) <= 0) {
      notify('Este producto requiere ingreso de precio variable. Se incorporará en la siguiente etapa.', 'info');
      return;
    }

    if (resolvedProduct.pesable) {
      setProductoPeso(resolvedProduct);
      setPesoEditIndex(null);
      setPesoInicialKg(null);
      setModalPeso(true);
      return;
    }

    setItems((current) => {
      const index = current.findIndex((item) => !item?.pesable && String(item.id) === String(resolvedProduct.id));
      if (index === -1) return [{ ...resolvedProduct, cantidad: 1 }, ...current];
      return current.map((item, i) => i === index ? { ...item, cantidad: Number(item.cantidad || 0) + 1 } : item);
    });
  }, [caja.abierta, notify]);

  const increase = useCallback((index) => setItems((rows) => rows.map((item, i) => {
    if (i !== index || item?.pesable) return item;
    return { ...item, cantidad: Number(item.cantidad || 0) + 1 };
  })), []);

  const decrease = useCallback((index) => setItems((rows) => rows.flatMap((item, i) => {
    if (i !== index) return [item];
    if (item?.pesable) return [item];
    const quantity = Number(item.cantidad || 0) - 1;
    return quantity > 0 ? [{ ...item, cantidad: quantity }] : [];
  })), []);

  const editWeight = useCallback((index) => {
    const item = items[index];
    if (!item?.pesable) return;
    openWeightModal(item, index);
  }, [items, openWeightModal]);

  const remove = useCallback((index) => setItems((rows) => rows.filter((_, i) => i !== index)), []);

  const total = useMemo(() => items.reduce((sum, item) => sum + Number(item.precioBruto || 0) * Number(item.cantidad || 0), 0), [items]);
  const impuestos = useMemo(() => items.reduce((sum, item) => {
    const gross = Number(item.precioBruto || 0) * Number(item.cantidad || 0);
    const pct = Number(item.impuestoPorcentaje || 0);
    if (pct <= 0) return sum;
    const net = Math.round(gross / (1 + pct / 100));
    return sum + (gross - net);
  }, 0), [items]);

  const filteredProducts = useMemo(() => {
    if (categoria && !modoBusqueda) return productos.slice(0, CATEGORY_LIMIT);
    const start = Math.max(0, (Number(pagina || 1) - 1) * CATEGORY_LIMIT);
    return productos.slice(start, start + CATEGORY_LIMIT);
  }, [categoria, modoBusqueda, pagina, productos]);
  const progresoInicial = useMemo(() => {
    const completados = INITIAL_LOAD_STEPS.filter(({ key }) => ['success', 'error'].includes(estadoCargaInicial[key]?.status)).length;
    return Math.round((completados / INITIAL_LOAD_STEPS.length) * 100);
  }, [estadoCargaInicial]);
  const selectedMethod = useMemo(() => metodosPago.find((method) => String(method.id) === String(metodoSeleccionado)) || null, [metodosPago, metodoSeleccionado]);
  const categoriaSeleccionada = useMemo(
    () => categoriasDisponibles.find((row) => String(row?.id ?? row?.categoria_id ?? '') === String(categoria || '')) || null,
    [categoriasDisponibles, categoria]
  );

  const restoreCurrentList = useCallback(async () => {
    if (categoria) {
      await loadCategory(categoria, pagina);
    } else {
      await loadFeatured();
    }
  }, [categoria, loadCategory, loadFeatured, pagina]);

  const handleCategorySelect = useCallback(async (category) => {
    const categoryId = String(category?.id ?? category?.categoria_id ?? '').trim();
    setBusqueda('');

    if (!categoryId) {
      setCategoria('');
      await loadFeatured();
      return;
    }

    setCategoria(categoryId);
    await loadCategory(categoryId, 1);
  }, [loadCategory, loadFeatured]);

  const handleCategoryPage = useCallback(async (nextPage) => {
    if (!categoria) return;
    await loadCategory(categoria, nextPage);
  }, [categoria, loadCategory]);

  const handleCatalogPage = useCallback(async (nextPage) => {
    const safePage = Math.max(1, Math.min(Number(nextPage) || 1, totalPaginas));
    if (safePage === pagina) return;

    if (categoria && !modoBusqueda) {
      await handleCategoryPage(safePage);
      return;
    }

    setPagina(safePage);
  }, [categoria, handleCategoryPage, modoBusqueda, pagina, totalPaginas]);

  const handleCatalogBack = useCallback(async () => {
    if (modoBusqueda) {
      setBusqueda('');
      setModoBusqueda(false);
      if (categoria) {
        await loadCategory(categoria, 1);
      } else {
        await loadFeatured();
      }
      return;
    }

    if (categoria) {
      setCategoria('');
      setBusqueda('');
      await loadFeatured();
    }
  }, [categoria, loadCategory, loadFeatured, modoBusqueda]);

  const clienteFacturaValidacion = useMemo(
    () => validarClienteFactura(cliente),
    [cliente]
  );

  const clienteEsGenerico = useMemo(() => {
    const rut = String(cliente?.rut || cliente?.rut_cliente || cliente?.rutCliente || '')
      .replace(/\./g, '')
      .trim()
      .toUpperCase();
    const genericRut = GENERIC_RUT.replace(/\./g, '').toUpperCase();
    return !cliente || rut === genericRut;
  }, [cliente]);

  const abrirSelectorClienteFactura = useCallback(() => {
    setModalClienteFactura(true);
  }, []);

  const abrirSelectorDocumento = useCallback((continuarPago = false) => {
    if (!items.length) {
      notify('Agregue al menos un producto antes de cobrar.', 'info');
      return;
    }

    if (!caja.abierta) {
      notify('Debe abrir caja antes de cobrar.', 'warning');
      return;
    }

    setDocumentoTemporal(documento || 'boleta');
    setContinuarPagoTrasDocumento(Boolean(continuarPago));
    setModalDocumento(true);
  }, [caja.abierta, documento, items.length, notify]);

  const confirmarDocumento = useCallback((tipo = documentoTemporal) => {
    const next = tipo === 'factura' ? 'factura' : 'boleta';

    if (next === 'factura') {
      setDocumento('factura');
      setDocumentoTemporal('factura');
      setModalDocumento(false);

      if (!clienteFacturaValidacion.valido || clienteEsGenerico) {
        setModalClienteFactura(true);
        return;
      }

      if (continuarPagoTrasDocumento) {
        setContinuarPagoTrasDocumento(false);
        window.setTimeout(() => setModalCobro(true), 40);
      }
      return;
    }

    setDocumento('boleta');
    setDocumentoTemporal('boleta');
    if (clienteGenerico) setCliente(clienteGenerico);
    setModalDocumento(false);

    if (continuarPagoTrasDocumento) {
      setContinuarPagoTrasDocumento(false);
      window.setTimeout(() => setModalCobro(true), 40);
    }
  }, [clienteEsGenerico, clienteFacturaValidacion.valido, clienteGenerico, continuarPagoTrasDocumento, documentoTemporal]);

  const seleccionarClienteFactura = useCallback((clienteSeleccionado) => {
    const validation = validarClienteFactura(clienteSeleccionado);
    if (!validation.valido) {
      notify(`El cliente no está listo para Factura: ${validation.faltantes.join(', ')}.`, 'warning');
      return;
    }

    setCliente(clienteSeleccionado);
    setDocumento('factura');
    setDocumentoTemporal('factura');
    setModalClienteFactura(false);

    const nombre = clienteSeleccionado?.razon_social ||
      [clienteSeleccionado?.nombres, clienteSeleccionado?.apellidos].filter(Boolean).join(' ') ||
      clienteSeleccionado?.rut ||
      'cliente';

    notify(`Cliente de factura seleccionado: ${nombre}.`, 'success');

    if (continuarPagoTrasDocumento) {
      setContinuarPagoTrasDocumento(false);
      window.setTimeout(() => setModalCobro(true), 40);
    }
  }, [continuarPagoTrasDocumento, notify]);

  const cerrarSelectorClienteFactura = useCallback(() => {
    setModalClienteFactura(false);

    if (continuarPagoTrasDocumento) {
      window.setTimeout(() => setModalDocumento(true), 40);
    }
  }, [continuarPagoTrasDocumento]);

  const handleWeightBarcode = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!WEIGHT_BARCODE_PATTERN.test(code)) return { handled: false, product: null };

    if (!caja.abierta) {
      notify('Debe abrir caja antes de agregar productos.', 'warning');
      return { handled: true, product: null };
    }

    const resolved = await resolveWeightBarcode(code);
    if (!resolved?.product) {
      notify(`No se pudo resolver la etiqueta de balanza ${code}. Revise el PLU del producto pesable.`, 'warning');
      return { handled: true, product: null };
    }

    if (!resolved.product.pesable) {
      notify('La etiqueta corresponde a un producto que no está marcado como pesable.', 'warning');
      return { handled: true, product: null };
    }

    const weightKg = roundWeightKg(resolved.weightKg);
    if (weightKg <= 0) {
      notify('La etiqueta de balanza contiene un peso inválido.', 'warning');
      return { handled: true, product: null };
    }

    addWeightedItem(resolved.product, weightKg, { source: 'balanza', barcode: code });
    setBusqueda('');
    return { handled: true, product: resolved.product, weightKg, parsed: resolved };
  }, [addWeightedItem, caja.abierta, notify]);

  const runProductLookup = useCallback(async (rawTerm, { fromScanner = false } = {}) => {
    const q = String(rawTerm || '').trim();
    if (!q) {
      await restoreCurrentList();
      return { rows: [], exact: null };
    }

    if (WEIGHT_BARCODE_PATTERN.test(q)) {
      const weighted = await handleWeightBarcode(q);
      if (weighted.handled) {
        return { rows: weighted.product ? [weighted.product] : [], exact: weighted.product || null, weighted };
      }
    }

    const rows = await searchProducts(q);
    const normalizedQuery = normalizeSearchText(q);
    const exact = rows.find((product) => normalizeSearchText(product.codigoBarras || '') === normalizedQuery)
      || rows.find((product) => normalizeSearchText(product.sku || '') === normalizedQuery);

    if (exact) {
      await addProduct(exact);
      setBusqueda('');
      await restoreCurrentList();
      return { rows, exact };
    }

    setBusqueda(q);

    if (fromScanner && !rows.length) {
      notify(`No se encontró un producto para el código ${q}.`, 'warning');
    }

    return { rows, exact: null };
  }, [addProduct, handleWeightBarcode, notify, restoreCurrentList, searchProducts]);

  const handleSearch = useCallback(async () => {
    const q = String(busqueda || '').trim();

    if (!q && items.length > 0) {
      abrirSelectorDocumento(true);
      return;
    }

    await runProductLookup(q);
  }, [abrirSelectorDocumento, busqueda, items.length, runProductLookup]);

  useEffect(() => {
    if (!modalDocumento) return undefined;

    const onDocumentKeyDown = (event) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        setDocumentoTemporal((current) => current === 'boleta' ? 'factura' : 'boleta');
        return;
      }

      if (event.key.toLowerCase() === 'b') {
        event.preventDefault();
        setDocumentoTemporal('boleta');
        return;
      }

      if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setDocumentoTemporal('factura');
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        confirmarDocumento();
      }
    };

    window.addEventListener('keydown', onDocumentKeyDown, true);
    return () => window.removeEventListener('keydown', onDocumentKeyDown, true);
  }, [confirmarDocumento, modalDocumento]);

  useEffect(() => {
    const handleCajaActualizada = () => {
      refreshCaja();
    };
    window.addEventListener('cajaActualizada', handleCajaActualizada);
    return () => window.removeEventListener('cajaActualizada', handleCajaActualizada);
  }, [refreshCaja]);

  useEffect(() => {
    const resetScannerBuffer = () => {
      scannerBufferRef.current = '';
      scannerLastKeyAtRef.current = 0;
      if (scannerTimerRef.current) {
        window.clearTimeout(scannerTimerRef.current);
        scannerTimerRef.current = null;
      }
    };

    const onGlobalKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (inicializando || modalCobro || modalDocumento || modalClienteFactura || modalCaja || modalPeso) return;

      const active = document.activeElement;
      const tagName = String(active?.tagName || '').toUpperCase();
      const isEditable = !!(active && (active.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)));
      if (isEditable) return;

      if (event.key === 'Enter') {
        const code = String(scannerBufferRef.current || '').trim();

        if (code.length >= 2 && !scannerBusyRef.current) {
          event.preventDefault();
          scannerBusyRef.current = true;
          setBusqueda(code);
          runProductLookup(code, { fromScanner: true })
            .finally(() => {
              scannerBusyRef.current = false;
            });
        } else if (!code && items.length > 0 && caja.abierta) {
          event.preventDefault();
          abrirSelectorDocumento(true);
        }

        resetScannerBuffer();
        return;
      }

      if (event.key.length !== 1) return;

      const now = Date.now();
      if (scannerLastKeyAtRef.current && now - scannerLastKeyAtRef.current > GLOBAL_CODE_GAP_MS) {
        scannerBufferRef.current = '';
      }

      scannerBufferRef.current += event.key;
      scannerLastKeyAtRef.current = now;

      if (scannerTimerRef.current) window.clearTimeout(scannerTimerRef.current);
      scannerTimerRef.current = window.setTimeout(() => {
        resetScannerBuffer();
      }, GLOBAL_CODE_IDLE_MS);
    };

    window.addEventListener('keydown', onGlobalKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onGlobalKeyDown, true);
      resetScannerBuffer();
    };
  }, [abrirSelectorDocumento, caja.abierta, inicializando, items.length, modalCaja, modalClienteFactura, modalCobro, modalDocumento, modalPeso, runProductLookup]);

  const handlePaymentSubmit = useCallback(async ({ pagos = [], vuelto = 0, redondeo = 0, pagoMixto = false }) => {
    if (procesandoVentaRef.current) {
      throw new Error('La venta ya se está registrando. Espere la respuesta del servidor.');
    }

    if (!navigator.onLine) {
      throw new Error('Sin conexión. Esta versión del contrato registra la venta online; no cierre el cobro hasta recuperar conexión.');
    }

    const paymentRows = Array.isArray(pagos) ? pagos : [];
    if (!paymentRows.length) {
      throw new Error('No se recibió información de pago para registrar la venta.');
    }

    if (documento === 'factura') {
      const validation = validarClienteFactura(cliente);
      if (!validation.valido) {
        setModalCobro(false);
        setModalClienteFactura(true);
        throw new Error(`Seleccione un cliente válido para Factura: ${validation.faltantes.join(', ')}.`);
      }
    }

    const signature = buildVentaPosCartSignature({
      items,
      documento,
      cliente,
      cajaId,
    });

    if (!ventaIntentoRef.current || ventaIntentoRef.current.signature !== signature) {
      ventaIntentoRef.current = {
        signature,
        clientSaleId: createVentaPosClientSaleId(),
      };
    }

    const clientSaleId = ventaIntentoRef.current.clientSaleId;

    procesandoVentaRef.current = true;
    setProcesandoVenta(true);

    try {
      const payload = buildVentaPosPayload({
        items,
        pagos: paymentRows,
        documento,
        cliente,
        cajaId,
        clientSaleId,
        pdfFormat: '80mm',
      });

      const result = await crearVentaPos(payload);
      const ventaId = result?.venta?.id_venta || null;
      const reused = result?.reused === true || result?.idempotency?.reused === true;
      const dteEmitido = Boolean(result?.dte);
      const documentoVendido = documento;

      let impresionSolicitada = false;
      let errorImpresion = null;

      if (ventaId && dteEmitido) {
        try {
          await imprimirDteVentaPos(result, {
            documento: documentoVendido,
            formato: '80mm',
          });
          impresionSolicitada = true;
        } catch (printError) {
          errorImpresion = printError;
          console.error('[PDV] La venta se registró, pero no se pudo imprimir el DTE:', printError);
        }
      }

      setModalCobro(false);
      setItems([]);
      setDocumento('boleta');
      setDocumentoTemporal('boleta');
      if (clienteGenerico) setCliente(clienteGenerico);
      ventaIntentoRef.current = null;

      const vueltoTexto = Number(vuelto || 0) > 0
        ? ` · vuelto $${Number(vuelto).toLocaleString('es-CL')}`
        : '';
      const redondeoTexto = Number(redondeo || 0) !== 0
        ? ` · redondeo ${Number(redondeo) > 0 ? '+' : ''}$${Number(redondeo).toLocaleString('es-CL')}`
        : '';

      notify(
        `${reused ? 'Venta recuperada' : 'Venta registrada'}${ventaId ? ` #${ventaId}` : ''}${dteEmitido ? ' · DTE emitido' : ''}${impresionSolicitada ? ' · impresión enviada' : ''}${pagoMixto ? ' · pago mixto' : ''}${vueltoTexto}${redondeoTexto}${errorImpresion ? ` · No se pudo imprimir: ${errorImpresion.message || 'error de impresión'}` : ''}.`,
        errorImpresion ? 'warning' : 'success'
      );

      // El movimiento de inventario ya fue ejecutado por ventas_pos. Se solicita
      // un delta en segundo plano para acercar el catálogo local al nuevo stock.
      clearCategoryMemoryCache(getTenantIdForCache());
      refreshPosProductsCache().catch(() => {});

      return result;
    } catch (error) {
      const info = getVentaPosError(error);
      const ventaParcial = info.ventaId ? ` · venta #${info.ventaId}` : '';

      notify(`${info.code}${ventaParcial}: ${info.message}`, info.ventaId ? 'warning' : 'error');

      // No borramos ventaIntentoRef: un reintento con el mismo carrito reutiliza
      // client_sale_id y ventas_pos puede responder idempotentemente sin duplicar.
      const friendlyError = new Error(info.message);
      friendlyError.code = info.code;
      friendlyError.details = info.details;
      throw friendlyError;
    } finally {
      procesandoVentaRef.current = false;
      setProcesandoVenta(false);
    }
  }, [cajaId, cliente, clienteGenerico, documento, items, notify]);

  return (
    <Box sx={{ minHeight: '100vh', height: { lg: '100dvh' }, overflow: { lg: 'hidden' }, bgcolor: '#edf3f8' }}>
      <NavbarPos
        usuario={usuario}
        cajaAbierta={caja.abierta}
        cajaId={cajaId}
        conectado={conectado}
        onCaja={() => setModalCaja(true)}
        onSalir={onSalir}
      />

      <Box sx={{ pt: { xs: '80px', lg: '82px' }, px: { xs: 1.2, md: 1.5, xl: 2 }, pb: { xs: 1.2, lg: 1.25 }, height: { lg: '100dvh' }, boxSizing: 'border-box', overflow: { lg: 'hidden' }, '@media (max-height: 820px)': { pt: '79px', pb: 0.8 } }}>
        <Box
          className="pos-main-layout"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(430px,.95fr) minmax(0,1.25fr)' },
            gap: { xs: 1, lg: 1.15, xl: 1.5 },
            alignItems: 'stretch',
            maxWidth: 1900,
            mx: 'auto',
            height: { lg: '100%' },
            minHeight: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #d8e2ec',
              borderRadius: 2.4,
              overflow: 'hidden',
              bgcolor: '#fff',
              minHeight: 0,
              height: { lg: '100%' },
              maxHeight: { lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 30px rgba(32, 58, 86, .075)',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Cart items={items} onAumentar={increase} onDisminuir={decrease} onEditarPeso={editWeight} onEliminar={remove} />
            </Box>
            <PaymentPanel
              total={total}
              impuestos={impuestos}
              metodosPago={metodosPago}
              metodoSeleccionado={metodoSeleccionado}
              onSeleccionarMetodo={(id) => setMetodoSeleccionado(String(id))}
              cliente={cliente}
              documento={documento}
              onCliente={abrirSelectorClienteFactura}
              onDocumento={() => abrirSelectorDocumento(false)}
              onCobrar={() => abrirSelectorDocumento(true)}
              disabled={!items.length || !selectedMethod || !caja.abierta || procesandoVenta}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              border: '1px solid #d4e1ef',
              borderRadius: 2.4,
              bgcolor: '#eef6ff',
              p: { xs: 1.05, md: 1.2, xl: 1.4 },
              minWidth: 0,
              minHeight: 0,
              height: { lg: '100%' },
              maxHeight: { lg: '100%' },
              '@media (max-height: 820px)': { p: 1 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(32, 58, 86, .075)',
            }}
          >
            <Stack spacing={1.05} sx={{ minHeight: 0, height: '100%', '@media (max-height: 820px)': { gap: 0.75 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.25, minHeight: 34, '@media (max-height: 820px)': { minHeight: 28 } }}>
                <Box>
                  <Typography sx={{ fontSize: { xs: 21, lg: 24 }, fontWeight: 1000, color: '#17355a', lineHeight: 1 }}>
                    Productos
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seleccione, busque o escanee para agregar a la venta
                  </Typography>
                </Box>
                <Typography variant="caption" fontWeight={850} sx={{ color: '#58718d', display: { xs: 'none', xl: 'block' } }}>
                  6 productos por página
                </Typography>
              </Stack>

              <SearchBar
                value={busqueda}
                onChange={(value) => {
                  setBusqueda(value);
                  if (!value.trim() && modoBusqueda) restoreCurrentList();
                }}
                onSearch={handleSearch}
                disabled={!caja.abierta}
              />

              <CategoryBar
                categories={categoriasDisponibles}
                selected={categoria}
                onSelect={handleCategorySelect}
                loading={cargandoCategorias}
              />

              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ProductGrid
                  productos={filteredProducts}
                  cargando={cargandoProductos}
                  onAgregar={addProduct}
                  pagina={pagina}
                  totalPaginas={totalPaginas}
                  onPagina={handleCatalogPage}
                />
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={inicializando}
        fullWidth
        maxWidth="xs"
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 2.6,
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(15, 23, 42, .22)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.35}>
            <Typography variant="h6" fontWeight={950}>Preparando Punto de Venta</Typography>
            <Typography variant="body2" color="text.secondary">
              Estamos dejando todo listo para comenzar a vender.
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>INICIALIZANDO</Typography>
                <Typography variant="caption" color="primary.main" fontWeight={900}>{progresoInicial}%</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progresoInicial}
                sx={{ height: 8, borderRadius: 999, bgcolor: '#e8eef7', '& .MuiLinearProgress-bar': { borderRadius: 999 } }}
              />
            </Box>

            <Stack spacing={1.15}>
              {INITIAL_LOAD_STEPS.map(({ key, label }) => {
                const step = estadoCargaInicial[key] || {};
                const isLoading = step.status === 'loading' || step.status === 'pending';
                const isSuccess = step.status === 'success';
                const isError = step.status === 'error';

                return (
                  <Stack key={key} direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                        bgcolor: isSuccess ? '#ecfdf5' : isError ? '#fff1f2' : '#eff6ff',
                        color: isSuccess ? '#059669' : isError ? '#dc2626' : '#0b6cf0',
                        fontWeight: 950,
                      }}
                    >
                      {isLoading ? <CircularProgress size={17} thickness={5} /> : isSuccess ? '✓' : '!'}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={900}>{label}</Typography>
                      <Typography
                        variant="caption"
                        color={isError ? 'error.main' : 'text.secondary'}
                        sx={{ display: 'block', lineHeight: 1.25 }}
                      >
                        {step.detail || 'En espera'}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>

            <Box sx={{ borderTop: '1px solid #e8eef7', pt: 1.5 }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: estadoCargaInicial.cache?.status === 'error' ? '#fff1f2' : '#f8fafc',
                    color: estadoCargaInicial.cache?.status === 'error' ? '#dc2626' : '#64748b',
                    flex: '0 0 auto',
                    fontWeight: 950,
                  }}
                >
                  {estadoCargaInicial.cache?.status === 'loading'
                    ? <CircularProgress size={17} thickness={5} />
                    : estadoCargaInicial.cache?.status === 'success'
                      ? '✓'
                      : estadoCargaInicial.cache?.status === 'error'
                        ? '!'
                        : '↻'}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={900}>Catálogo local</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.25 }}>
                    {estadoCargaInicial.cache?.detail || 'Se sincronizará en segundo plano'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'center' }}>
            IntegraNube PDV
          </Typography>
        </DialogActions>
      </Dialog>

      <CajaModal
        open={modalCaja}
        onClose={() => setModalCaja(false)}
        onUpdated={async () => {
          await refreshCaja();
        }}
        onNotify={notify}
        bloquearCierre={items.length > 0}
      />

      <WeightModal
        open={modalPeso}
        product={productoPeso}
        initialWeightKg={pesoInicialKg}
        editing={Number.isInteger(pesoEditIndex)}
        onClose={closeWeightModal}
        onConfirm={confirmWeight}
      />

      <ClienteFacturaModal
        open={modalClienteFactura}
        clienteActual={clienteFacturaValidacion.valido ? cliente : null}
        onClose={cerrarSelectorClienteFactura}
        onSelect={seleccionarClienteFactura}
      />

      <PaymentModal
        open={modalCobro}
        onClose={() => {
          if (!procesandoVenta) setModalCobro(false);
        }}
        total={total}
        metodosPago={metodosPago}
        metodoSeleccionado={metodoSeleccionado}
        onSeleccionarMetodo={(id) => setMetodoSeleccionado(String(id))}
        onConfirmar={handlePaymentSubmit}
        procesando={procesandoVenta}
      />

      <Dialog
        open={modalDocumento}
        onClose={() => {
          setModalDocumento(false);
          setContinuarPagoTrasDocumento(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.6 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" fontWeight={950}>Seleccione documento</Typography>
          <Typography variant="body2" color="text.secondary">← → para cambiar · Enter para continuar</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1.2} sx={{ pt: 1.2 }}>
            <Button
              fullWidth
              variant={documentoTemporal === 'boleta' ? 'contained' : 'outlined'}
              onClick={() => setDocumentoTemporal('boleta')}
              onDoubleClick={() => confirmarDocumento('boleta')}
              sx={{ minHeight: 92, borderRadius: 1.8, fontSize: 18, fontWeight: 950 }}
            >
              Boleta
            </Button>
            <Button
              fullWidth
              variant={documentoTemporal === 'factura' ? 'contained' : 'outlined'}
              onClick={() => setDocumentoTemporal('factura')}
              onDoubleClick={() => confirmarDocumento('factura')}
              sx={{ minHeight: 92, borderRadius: 1.8, fontSize: 18, fontWeight: 950 }}
            >
              Factura
            </Button>
          </Stack>

          {documentoTemporal === 'factura' ? (
            <Box
              sx={{
                mt: 1.25,
                p: 1.2,
                border: '1px solid',
                borderColor: clienteFacturaValidacion.valido ? '#bbf7d0' : '#f3d7a8',
                bgcolor: clienteFacturaValidacion.valido ? '#f0fdf4' : '#fffaf0',
                borderRadius: 1.5,
              }}
            >
              {clienteFacturaValidacion.valido ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Cliente factura</Typography>
                    <Typography variant="body2" fontWeight={950} noWrap>
                      {cliente?.razon_social || [cliente?.nombres, cliente?.apellidos].filter(Boolean).join(' ') || cliente?.rut}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{cliente?.rut}</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={abrirSelectorClienteFactura}>Cambiar</Button>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" fontWeight={850} color="warning.dark">
                    Debe seleccionar un cliente con datos tributarios completos.
                  </Typography>
                  <Button size="small" variant="contained" onClick={abrirSelectorClienteFactura}>Seleccionar cliente</Button>
                </Stack>
              )}
            </Box>
          ) : null}

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">B = Boleta · F = Factura</Typography>
            <Typography variant="caption" color="primary.main" fontWeight={900}>Enter → continuar</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setModalDocumento(false); setContinuarPagoTrasDocumento(false); }}>Cancelar</Button>
          <Button variant="contained" onClick={() => confirmarDocumento()}>Continuar</Button>
        </DialogActions>
      </Dialog>

      {!inicializando && !caja.abierta && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => setModalCaja(true)}>
              Abrir caja
            </Button>
          }
          sx={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1200, maxWidth: 560 }}
        >
          La caja está cerrada. Puede revisar el catálogo, pero debe abrir caja para vender.
        </Alert>
      )}

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((current) => ({ ...current, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
